import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

/**
 * Convert markdown tables to HTML tables
 */
const preprocessTables = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  // More flexible table regex pattern for basic markdown tables
  // Matches tables with or without leading/trailing spaces
  const tableRegex = /^\s*(\|[^\n]+\|)\s*\n\s*(\|[\s:|-]+\|)\s*\n((\s*\|[^\n]*\|\s*\n?)*)/gm;
  
  let result = text.replace(tableRegex, (match, header, separator, rows) => {
    console.log('Table match found:', { match, header, separator, rows });
    
    // Parse header
    const headerCells = header.split('|')
      .slice(1, -1)
      .map(cell => cell.trim())
      .filter(cell => cell.length > 0);
    
    if (headerCells.length === 0) return match; // Invalid table
    
    // Parse rows
    const rowLines = rows.trim().split('\n')
      .filter(line => line.trim() && line.includes('|'));
    
    const rowData = rowLines.map(row => {
      const cells = row.split('|')
        .slice(1, -1)
        .map(cell => cell.trim());
      // Pad with empty cells if needed
      while (cells.length < headerCells.length) {
        cells.push('');
      }
      return cells.slice(0, headerCells.length);
    }).filter(row => row.length > 0);
    
    console.log('Parsed table data:', { headerCells, rowData });
    
    // Generate HTML table with direct styling
    let html = '<table style="border-collapse: collapse; width: 100%; max-width: 100%; height: 400px; min-height: 200px; min-width: 300px; overflow: auto; margin: 16px 0; border: 1px solid #e0e0e0; border-radius: 4px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); position: relative; resize: both; font-size: 14px; display: block; table-layout: auto;">\n';
    
    // Header
    html += '<thead style="background-color: #f8f9fa;">\n<tr>\n';
    headerCells.forEach(cell => {
      html += `<th style="border: 1px solid #e0e0e0; padding: 12px 16px; text-align: left; font-weight: bold; background-color: #f8f9fa; color: #6c0c46; white-space: nowrap; min-width: 120px; position: sticky; top: 0; z-index: 10;">${cell}</th>\n`;
    });
    html += '</tr>\n</thead>\n';
    
    // Body
    if (rowData.length > 0) {
      html += '<tbody>\n';
      rowData.forEach(row => {
        html += '<tr>\n';
        row.forEach(cell => {
          html += `<td style="border: 1px solid #e0e0e0; padding: 12px 16px; text-align: left; min-width: 120px;">${cell}</td>\n`;
        });
        html += '</tr>\n';
      });
      html += '</tbody>\n';
    }
    
    html += '</table>\n';
    
    console.log('Generated HTML:', html);
    return html;
  });
  
  return result;
};

/**
 * Reusable component for rendering markdown text inline
 * Useful for titles, short descriptions, and other inline content
 */
const MarkdownText = ({ children, inline = true }) => {
  // Preprocess text to handle incomplete markdown patterns and tables
  let processedText = children || '';
  
  // Convert [text] to **text** for bold formatting if it's not a complete link
  if (typeof processedText === 'string') {
    // Only convert [text] to **text** if it's not followed by (url) or [ref]
    processedText = processedText.replace(/\[([^\]]+)\](?!\(|\[)/g, '**$1**');
    
    // Preprocess tables if not inline mode
    if (!inline) {
      console.log('Processing tables for text:', processedText.substring(0, 200));
      processedText = preprocessTables(processedText);
      console.log('After table processing:', processedText.substring(0, 200));
    }
  }

  const inlineComponents = {
    p: ({ children }) => <span>{children}</span>,
    h1: ({ children }) => <span>{children}</span>,
    h2: ({ children }) => <span>{children}</span>,
    h3: ({ children }) => <span>{children}</span>,
    h4: ({ children }) => <span>{children}</span>,
    h5: ({ children }) => <span>{children}</span>,
    h6: ({ children }) => <span>{children}</span>,
    strong: ({ children }) => <strong>{children}</strong>,
    em: ({ children }) => <em>{children}</em>,
    code: ({ children }) => (
      <code 
        style={{ 
          backgroundColor: 'rgba(108, 12, 70, 0.1)', 
          padding: '2px 4px', 
          borderRadius: '3px', 
          fontSize: '0.9em' 
        }}
      >
        {children}
      </code>
    ),
    del: ({ children }) => <del>{children}</del>,
    a: ({ href, children }) => (
      <a 
        href={href} 
        style={{ 
          color: '#6c0c46', 
          textDecoration: 'underline' 
        }} 
        target="_blank" 
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    // Remove block elements for inline rendering
    ul: ({ children }) => <span>{children}</span>,
    ol: ({ children }) => <span>{children}</span>,
    li: ({ children }) => <span>{children} </span>,
    blockquote: ({ children }) => <span>{children}</span>,
    br: () => <span> </span>
  };

  const blockComponents = {
    strong: ({ children }) => <strong>{children}</strong>,
    em: ({ children }) => <em>{children}</em>,
    code: ({ children }) => (
      <code 
        style={{ 
          backgroundColor: 'rgba(108, 12, 70, 0.1)', 
          padding: '2px 4px', 
          borderRadius: '3px', 
          fontSize: '0.9em' 
        }}
      >
        {children}
      </code>
    ),
    del: ({ children }) => <del>{children}</del>,
    a: ({ href, children }) => (
      <a 
        href={href} 
        style={{ 
          color: '#6c0c46', 
          textDecoration: 'underline' 
        }} 
        target="_blank" 
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    h1: ({ children }) => <h1 style={{ color: '#6c0c46' }}>{children}</h1>,
    h2: ({ children }) => <h2 style={{ color: '#6c0c46' }}>{children}</h2>,
    h3: ({ children }) => <h3 style={{ color: '#6c0c46' }}>{children}</h3>,
    h4: ({ children }) => <h4 style={{ color: '#6c0c46' }}>{children}</h4>,
    h5: ({ children }) => <h5 style={{ color: '#6c0c46' }}>{children}</h5>,
    h6: ({ children }) => <h6 style={{ color: '#6c0c46' }}>{children}</h6>,
    blockquote: ({ children }) => (
      <blockquote 
        style={{ 
          borderLeft: '4px solid #6c0c46', 
          paddingLeft: '16px', 
          margin: '16px 0',
          fontStyle: 'italic',
          color: '#666'
        }}
      >
        {children}
      </blockquote>
    ),
    ul: ({ children }) => <ul style={{ paddingLeft: '20px' }}>{children}</ul>,
    ol: ({ children }) => <ol style={{ paddingLeft: '20px' }}>{children}</ol>,
    li: ({ children }) => <li style={{ marginBottom: '4px' }}>{children}</li>,
    table: ({ children }) => (
      <table 
        style={{ 
          borderCollapse: 'collapse',
          width: '100%',
          maxWidth: '100%',
          height: '400px',
          minHeight: '200px',
          minWidth: '300px',
          overflow: 'auto',
          margin: '16px 0', 
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          position: 'relative',
          resize: 'both',
          fontSize: '14px',
          display: 'block',
          tableLayout: 'auto'
        }}
      >
        {children}
      </table>
    ),
    thead: ({ children }) => (
      <thead style={{ backgroundColor: '#f8f9fa' }}>
        {children}
      </thead>
    ),
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => <tr>{children}</tr>,
    th: ({ children }) => (
      <th 
        style={{ 
          border: '1px solid #e0e0e0',
          padding: '12px 16px',
          textAlign: 'left',
          fontWeight: 'bold',
          backgroundColor: '#f8f9fa',
          color: '#6c0c46',
          whiteSpace: 'nowrap',
          minWidth: '120px',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}
      >
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td 
        style={{ 
          border: '1px solid #e0e0e0',
          padding: '12px 16px',
          textAlign: 'left',
          minWidth: '120px'
        }}
      >
        {children}
      </td>
    )
  };

  return (
    <ReactMarkdown 
      components={inline ? inlineComponents : blockComponents}
      rehypePlugins={inline ? [] : [rehypeRaw]}
      remarkPlugins={[]}
    >
      {processedText}
    </ReactMarkdown>
  );
};

export default MarkdownText;
